import { memo, useRef, useMemo, useCallback, useContext, useEffect, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Crud, CrudFormComponent, CrudMode, CrudProps, FormItem, getSelectsOptions, SelectsOptions } from '@ohpen/enso-ui';
import { ProductResponse } from '@pmp/api';
import { PmpConfigContext } from '@pmp/contexts';
import { flattenObjectValues } from '@ohpen/react-utils';

import { getFormSchema } from '../formConfig';
import { ProductsViewProps } from '../interfaces';
import { getAdditionalFieldsSchema } from '../utils';
import { ProductsFormSchema } from './ProductsFormSchema';

export type DataType = ProductResponse;

export const ProductView = memo(({ record, valueLists, additionalFields, onClose }: ProductsViewProps): ReactElement => {
  const ref = useRef<CrudFormComponent>(null);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { isBoSyncActive } = useContext(PmpConfigContext);

  useEffect(() => {
    setOpen(true);
  }, []);

  const initialValues = useMemo(() => {
    return flattenObjectValues(record ?? {});
  }, [record]);

  const productCode = record?.code;

  const schema: FormItem[] = useMemo(() => {
    const selectsOptions: SelectsOptions = (valueLists && getSelectsOptions(valueLists)) || {};

    return getFormSchema(selectsOptions, { boSyncProductCode: isBoSyncActive ? productCode : undefined });
  }, [valueLists, isBoSyncActive, productCode]);

  const additionalFieldsSchema: FormItem | null = useMemo(() => {
    return getAdditionalFieldsSchema(additionalFields);
  }, [additionalFields]);

  const onConfirm = useCallback(() => Promise.resolve(), []);

  const handleOnClose = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  const crudProps: CrudProps<DataType> = useMemo(() => {
    return {
      ref,
      open,
      title: t('global.labels.viewX', { x: t('pmp.products.labels.product') }),
      initialValues,
      mode: CrudMode.VIEW,
      readonly: true,
      customForm: true,
      width: 1100,
      disableDrawerBodyScroll: true,
      onClose: handleOnClose,
      onConfirm,
    };
  }, [open, t, initialValues, handleOnClose, onConfirm]);

  return (
    <Crud {...crudProps}>
      <ProductsFormSchema
        form={ref.current?.form}
        schema={schema}
        additionalFieldsSchema={additionalFieldsSchema}
        mode={CrudMode.VIEW}
        readonly
      />
    </Crud>
  );
});
